import { motion } from 'framer-motion';
import { Search, User, Users } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { h4, p, sh2, sh3, textStyle } from '../../styles/typography';
import Card from '../ui/card';
import './HyperLocalReferralTracker.css';

const ReferralList = ({ referrals = [], onSelectInfluencer, loading }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredReferrals = useMemo(() => {
    if (!searchQuery.trim()) return referrals;
    const q = searchQuery.toLowerCase();
    return referrals.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.referral_code?.toLowerCase().includes(q) ||
        r.location?.toLowerCase().includes(q) ||
        r.region?.toLowerCase().includes(q) ||
        r.site_name?.toLowerCase().includes(q) ||
        r.site_id?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q)
    );
  }, [referrals, searchQuery]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  if (loading) {
    return (
      <div className="referral-tracker-list referral-tracker-loading">
        <div className="referral-tracker-spinner" />
        <p className={textStyle(p, 'text-gray-600 mt-4')}>Loading referral data...</p>
      </div>
    );
  }

  return (
    <div className="referral-tracker-list">
      <div className="referral-tracker-search-wrap">
        <div className="referral-tracker-search-inner">
          <Search className="referral-tracker-search-icon" />
          <input
            type="text"
            placeholder="Search by name, code, location or site..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="referral-tracker-search-input"
          />
        </div>
      </div>

      <div className="referral-tracker-cards">
        {filteredReferrals.length === 0 ? (
          <Card variant="light" className="referral-tracker-empty-card p-6">
            <p className={textStyle(p, 'text-gray-600')}>
              {searchQuery ? 'No influencers match your search.' : 'No referral data yet.'}
            </p>
          </Card>
        ) : (
          filteredReferrals.map((item) => (
            <motion.div
              key={item.influencer_id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="referral-tracker-card-wrap"
            >
              <Card
                variant="light"
                className="referral-tracker-card cursor-pointer hover:border-teal-200 transition-colors"
                onClick={() => onSelectInfluencer && onSelectInfluencer(item)}
              >
                <div className="referral-tracker-card-header">
                  <h3 className={textStyle(h4, 'referral-tracker-card-title')}>
                    {item.name || 'Unnamed Influencer'}
                  </h3>
                  <span
                    className={`referral-tracker-status referral-tracker-status--${item.status || 'active'}`}
                  >
                    {item.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="referral-tracker-card-body">
                  {(item.referral_code != null && item.referral_code !== '') && (
                    <div className="referral-tracker-meta-row">
                      <User className="referral-tracker-meta-icon" />
                      <span className={textStyle(sh3, 'referral-tracker-meta-text')}>
                        Code: {item.referral_code}
                      </span>
                    </div>
                  )}
                  {/* <div className="referral-tracker-meta-row">
                    <MapPin className="referral-tracker-meta-icon" />
                    <span className={textStyle(p, 'referral-tracker-meta-text')}>
                      {[item.location, item.region].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </div> */}
                  {(item.site_name ?? item.site_id) && (
                    <div className="referral-tracker-meta-row">
                      <span className={textStyle(p, 'referral-tracker-meta-text text-gray-600')}>
                        Site: {item.site_name ?? item.site_id}
                      </span>
                    </div>
                  )}
                  {(item.email ?? item.phone) && (
                    <div className="referral-tracker-meta-row">
                      <span className={textStyle(p, 'referral-tracker-meta-text text-gray-600')}>
                        {[item.email, item.phone].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="referral-tracker-card-footer">
                  <div className="referral-tracker-stat">
                    <Users className="referral-tracker-stat-icon" />
                    <span className={textStyle(sh2, 'referral-tracker-stat-value')}>
                      {item.referral_count ?? 0}
                    </span>
                    <span className={textStyle(p, 'referral-tracker-stat-label')}>referrals</span>
                  </div>
                  <span className={textStyle(p, 'referral-tracker-date text-gray-500')}>
                    Last: {formatDate(item.last_referral_at)}
                  </span>
                </div>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReferralList;
